import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { leaveStep } from "@/lib/nav";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2, ArrowLeft, Save, Check, Truck, PackagePlus } from "lucide-react";
import { PhotoViewer } from "@/components/PhotoViewer";
import { TruckSpecsPanel } from "@/components/TruckSpecsPanel";
import { VinDecodedFields } from "@/components/VinDecodedFields";
import { CreatePartFromTruck } from "@/components/CreatePartFromTruck";
import { useDonorStatuses, useDonorStageLabel } from "@/lib/vehicleLists";

import { titleCase } from "@/lib/text";
import { ScanTextInput } from "@/components/ScanTextInput";

// Dedicated EDIT WORKSPACE (parts-parity split): the DISPLAY page shows the truck cleanly;
// this page is built for data entry — big photo viewer on the left (cycle/zoom/pan/rotate
// while you type), all editable fields (basic details + component-grouped specs + Ask AI) on
// the right. "Done" returns to the formatted display page.
export default function DonorEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Edit and Done are not pages (29 Oct): Done goes back to the truck itself, never a second copy of it
  const done = () => leaveStep(navigate, location, `/donors/${id}`);
  const { has } = useAuth();
  const stages = useDonorStatuses();
  const donorStatusLabel = useDonorStageLabel();
  const canViewCost = has("view_acquisition_cost");
  const canManage = has("manage_donors");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [stageSaving, setStageSaving] = useState(false);
  const [newPartOpen, setNewPartOpen] = useState(false);
  const canParts = has("manage_parts");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/donors/${id}/detail`);
      setData(data);
      const d = data.donor;
      setForm({
        vin: d.vin || "", asset_class: d.asset_class || "vehicle", year: d.year || "",
        make: d.make || "", model: d.model || "", engine_model: d.engine_model || "",
        vin_extra: { ...(d.vin_extra || {}) },
        mileage: d.mileage || "", hours: d.hours || "",
        purchase_price: d.purchase_price ?? "", landed_cost: d.landed_cost ?? "", notes: d.notes || "",
      });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setData(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // THE SILENT OVERWRITE THAT USED TO LIVE HERE: a Decode button that set the form's engine to
  // the decoded engine "or else whatever was already in the box" — so the decoded value always
  // won, a correction could be undone without a word, and nothing recorded that it had happened.
  // Re-decoding is now a SERVER action (`POST /donors/{id}/apply-decode`) that fills only what is
  // empty or still the decode's own, leaves every human value alone, and says what it left and why.

  const saveDetails = async () => {
    const d = data.donor;
    const vinChanged = (form.vin || "").trim().toUpperCase() !== (d?.vin || "").trim().toUpperCase();
    if (form.asset_class !== "equipment" && vinChanged) {
      const v = (form.vin || "").trim().toUpperCase();
      const issues = [];
      if (v.length !== 17) issues.push(`VIN is ${v.length} characters — a vehicle VIN must be exactly 17.`);
      const bad = ["I", "O", "Q"].filter((c) => v.includes(c));
      if (bad.length) issues.push(`VIN contains ${bad.join(", ")} — the letters I, O and Q are never used in a VIN.`);
      if (issues.length) { toast.error(issues.join(" ")); return; }
    }
    setSaving(true);
    const body = { ...form, purchase_price: form.purchase_price === "" ? null : Number(form.purchase_price),
      landed_cost: form.landed_cost === "" ? null : Number(form.landed_cost), mileage: form.mileage || null, hours: form.hours || null };
    try { await api.put(`/donors/${id}`, body); toast.success("Details saved"); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const setStage = async (status) => {
    if (status === data.donor.status) return;
    setStageSaving(true);
    try { await api.post(`/donors/${id}/status`, { status }); toast.success(`Stage → ${donorStatusLabel(status)}`); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setStageSaving(false); }
  };

  if (loading || !form) return <div data-testid="donor-edit-loading" className="p-16 text-center font-mono text-xs text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />LOADING…</div>;
  if (!data) return <div data-testid="donor-edit-missing" className="p-16 text-center text-sm text-slate-500">Truck not found. <Link to="/donors" className="text-[#E0781A] underline">Back to Vehicles &amp; Equipment</Link></div>;

  const d = data.donor;
  const vehicle = [d.year, titleCase(d.make), titleCase(d.model)].filter(Boolean).join(" ") || "Unspecified vehicle";
  const stageCodes = stages.length ? stages.map((s) => s.code) : [d.status].filter(Boolean);

  return (
    <div data-testid="donor-edit-page" className="max-w-[1600px] mx-auto">
      {/* header */}
      <div className="flex items-center gap-3 mb-4">
        <Button data-testid="donor-edit-back" variant="outline" onClick={done} className="rounded-lg h-9"><ArrowLeft className="w-4 h-4 mr-1.5" />Done</Button>
        <div className="min-w-0">
          <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400">Editing · {d.stock_number}</div>
          <h1 data-testid="donor-edit-title" className="font-display text-xl sm:text-2xl font-black tracking-tight text-slate-900 truncate">{vehicle}</h1>
        </div>
        <span className="flex-1" />
        {canParts && (
          <Button data-testid="donor-edit-new-part" onClick={() => setNewPartOpen(true)} variant="outline" className="rounded-lg h-9 border-[#B01E28] text-[#B01E28] hover:bg-red-50"><PackagePlus className="w-4 h-4 mr-1.5" />New part</Button>
        )}
        <Button data-testid="donor-edit-done" onClick={done} className="rounded-lg bg-slate-800 hover:bg-slate-900 text-white h-9"><Check className="w-4 h-4 mr-1.5" />Finish</Button>
      </div>

      <div className="flex flex-col lg:flex-row gap-5 items-start">
        {/* LEFT — photo viewer front-and-center (parts-parity) */}
        <div className="w-full lg:w-3/5 lg:sticky lg:top-4">
          <PhotoViewer photos={d.photos} testidPrefix="donor-edit" heightClass="h-[46dvh] lg:h-[calc(100vh-11rem)]"
            fixScope="donor" fixRecordId={id} onFixed={load} />
        </div>

        {/* RIGHT — the entry workspace */}
        <div className="w-full lg:w-2/5 space-y-5">
          {/* basic details */}
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex items-center gap-2 mb-3"><Truck className="w-4 h-4 text-[#E0781A]" /><span className="font-semibold text-sm">Truck details</span></div>
            {canManage ? (
              <div className="space-y-3">
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">VIN</label>
                  <ScanTextInput testid="donor-edit-vin" value={form.vin}
                    onChange={(v) => setForm({ ...form, vin: v.toUpperCase() })}
                    inputClassName="h-9" className="mt-1" placeholder="17-char VIN" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Mileage</label><Input data-testid="donor-edit-mileage" value={form.mileage} onChange={(e) => setForm({ ...form, mileage: e.target.value })} className="rounded-lg h-9 mt-1" /></div>
                  <div><label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Hours</label><Input data-testid="donor-edit-hours" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} className="rounded-lg h-9 mt-1" /></div>
                </div>
                {canViewCost && (
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Purchase $</label><Input data-testid="donor-edit-purchase" type="number" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} className="rounded-lg h-9 mt-1" /></div>
                    <div><label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Landed $</label><Input data-testid="donor-edit-landed" type="number" value={form.landed_cost} onChange={(e) => setForm({ ...form, landed_cost: e.target.value })} className="rounded-lg h-9 mt-1" /></div>
                  </div>
                )}
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Stage</label>
                  <Select value={d.status} onValueChange={setStage} disabled={stageSaving}>
                    <SelectTrigger data-testid="donor-edit-stage" className="rounded-lg h-9 mt-1 text-xs font-mono uppercase w-full"><SelectValue>{donorStatusLabel(d.status)}</SelectValue></SelectTrigger>
                    <SelectContent>{stageCodes.map((s) => <SelectItem key={s} value={s} className="text-xs font-mono uppercase">{donorStatusLabel(s)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Notes</label>
                  <textarea data-testid="donor-edit-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full rounded-lg border border-slate-200 p-2 text-sm mt-1" />
                </div>
                <Button data-testid="donor-edit-save-details" onClick={saveDetails} disabled={saving} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] w-full">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}Save details</Button>
              </div>
            ) : <p className="text-sm text-slate-400">You don't have permission to edit truck details.</p>}
          </div>

          {/* EVERY FIELD THE VIN DECODE CAN FILL — all editable, all stamped. Engine lives here. */}
          <VinDecodedFields donor={d} fields={data.decoded_fields || []} form={form} setForm={setForm}
            canEdit={canManage} onSaved={load} />

          {/* component-grouped specs + Ask AI + Create Part (the centerpiece) */}
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <TruckSpecsPanel donorId={id} canEdit={canManage} donor={d} onCategoryChange={load} />
          </div>
        </div>
      </div>

      {canParts && (
        <CreatePartFromTruck open={newPartOpen} onOpenChange={setNewPartOpen} donorId={id} donorStock={d.stock_number}
          onCreated={() => { setNewPartOpen(false); load(); }} />
      )}
    </div>
  );
}
